// ── Grenade Visual Effects ──
// HE explosion, smoke cloud, flash screen overlay, and fire zone effects.
// Follows existing patterns from effect-loop.ts and effect-projectile.ts.

import * as THREE from 'three';
import {
  addEffect,
  createSpriteTexture,
  markEffect,
  disposeMesh,
  rand,
} from './effect-loop';
import { triggerScreenShake } from './effects';
import { SMOKE_DURATION, SMOKE_RADIUS, FIRE_DURATION, FIRE_RADIUS, HE_BLAST_RADIUS, DECOY_DURATION, DECOY_GUNFIRE_INTERVAL } from '../core/config';
import { registerSmokeCloud, unregisterSmokeCloud, type SmokeFogCloud } from './smoke-fog';

// ---------------------------------------------------------------------------
// Material pools — reuse SpriteMaterial instances across effect lifetimes
// ---------------------------------------------------------------------------

/**
 * Generic pool of SpriteMaterial instances. Materials are acquired on spawn
 * and returned when the effect expires — eliminating repeated allocations.
 */
class SpriteMaterialPool {
  private available: THREE.SpriteMaterial[] = [];
  private overflow: THREE.SpriteMaterial[] = [];
  private capacity: number;
  private factory: () => THREE.SpriteMaterial;

  constructor(capacity: number, factory: () => THREE.SpriteMaterial) {
    this.capacity = capacity;
    this.factory = factory;
    // Pre-fill the pool
    for (let i = 0; i < capacity; i++) {
      this.available.push(factory());
    }
  }

  /** Acquire a material from the pool. Creates a new one if pool is empty. */
  acquire(): THREE.SpriteMaterial {
    if (this.available.length > 0) {
      return this.available.pop()!;
    }
    // Pool exhausted — create overflow material and track it for disposal
    const mat = this.factory();
    this.overflow.push(mat);
    return mat;
  }

  /** Return a material to the pool. Resets opacity and color. */
  release(mat: THREE.SpriteMaterial): void {
    // Remove from overflow tracking if present
    const overflowIdx = this.overflow.indexOf(mat);
    if (overflowIdx !== -1) {
      this.overflow.splice(overflowIdx, 1);
    }

    if (this.available.length < this.capacity) {
      mat.opacity = 1;
      mat.color.setRGB(1, 1, 1);
      mat.rotation = 0;
      this.available.push(mat);
    } else {
      // Over capacity (overflow material) — dispose it
      mat.dispose();
    }
  }

  /** Dispose all pooled materials (including any unreturned overflow materials). */
  dispose(): void {
    for (const m of this.available) m.dispose();
    this.available.length = 0;
    for (const m of this.overflow) m.dispose();
    this.overflow.length = 0;
  }
}

// Smoke cloud material pool (50 per cloud, but multiple clouds can exist)
let _smokeMaterialPool: SpriteMaterialPool | null = null;
function getSmokeMaterialPool(): SpriteMaterialPool {
  if (!_smokeMaterialPool) {
    _smokeMaterialPool = new SpriteMaterialPool(100, () =>
      new THREE.SpriteMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
  }
  return _smokeMaterialPool;
}

// Fire sprite material pool (24 per fire zone + 35 per HE explosion fire particles)
let _fireMaterialPool: SpriteMaterialPool | null = null;
function getFireMaterialPool(): SpriteMaterialPool {
  if (!_fireMaterialPool) {
    _fireMaterialPool = new SpriteMaterialPool(80, () =>
      new THREE.SpriteMaterial({
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 1,
        depthWrite: false,
      }),
    );
  }
  return _fireMaterialPool;
}

// HE smoke-column material pool (8 per explosion)
let _heSmokeMaterialPool: SpriteMaterialPool | null = null;
function getHeSmokeMaterialPool(): SpriteMaterialPool {
  if (!_heSmokeMaterialPool) {
    _heSmokeMaterialPool = new SpriteMaterialPool(24, () =>
      new THREE.SpriteMaterial({
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
      }),
    );
  }
  return _heSmokeMaterialPool;
}

// ---------------------------------------------------------------------------
// Shared debris geometries and materials for HE explosions (R13).
// F-RN-002: lazy-init lowercase variables so disposeEffectPools() can null
// them without breaking subsequent Game() instances. The legacy uppercase
// module-level constants no longer exist; rendering-bugs.test.ts asserts
// the file is free of those identifiers as a regression guard. With
// lazy-init the disposal is safe: getDebrisResources() rebuilds them on
// next spawn.
// ---------------------------------------------------------------------------

let _debrisGeometries: THREE.BoxGeometry[] = [];
let _debrisMaterials: THREE.MeshLambertMaterial[] = [];

function getDebrisResources(): {
  geos: THREE.BoxGeometry[];
  mats: THREE.MeshLambertMaterial[];
} {
  if (_debrisGeometries.length === 0) {
    _debrisGeometries = [
      new THREE.BoxGeometry(0.03, 0.03, 0.08),
      new THREE.BoxGeometry(0.05, 0.02, 0.05),
      new THREE.BoxGeometry(0.04, 0.04, 0.04),
      new THREE.BoxGeometry(0.06, 0.02, 0.03),
      new THREE.BoxGeometry(0.02, 0.06, 0.04),
      new THREE.BoxGeometry(0.05, 0.03, 0.06),
      new THREE.BoxGeometry(0.03, 0.05, 0.03),
      new THREE.BoxGeometry(0.04, 0.02, 0.07),
    ];
  }
  if (_debrisMaterials.length === 0) {
    _debrisMaterials = [
      new THREE.MeshLambertMaterial({ color: 0x3a2a1a, transparent: true, opacity: 1 }), // brown
      new THREE.MeshLambertMaterial({ color: 0x2a2a2a, transparent: true, opacity: 1 }), // dark gray
      new THREE.MeshLambertMaterial({ color: 0x4a3a2a, transparent: true, opacity: 1 }), // tan
      new THREE.MeshLambertMaterial({ color: 0x1a1a1a, transparent: true, opacity: 1 }), // charcoal
    ];
  }
  return { geos: _debrisGeometries, mats: _debrisMaterials };
}

/**
 * Dispose all global pools and shared resources.
 * Call on application shutdown or when no more effects are needed.
 */
export function disposeEffectPools(): void {
  _smokeMaterialPool?.dispose();
  _smokeMaterialPool = null;
  _fireMaterialPool?.dispose();
  _fireMaterialPool = null;
  _heSmokeMaterialPool?.dispose();
  _heSmokeMaterialPool = null;
  // Drop debris GPU resources too — they're rebuilt lazily on the next HE
  // spawn via getDebrisResources(), so a Game.dispose() + new Game() cycle
  // never inherits disposed buffers.
  for (const g of _debrisGeometries) g.dispose();
  _debrisGeometries = [];
  for (const m of _debrisMaterials) m.dispose();
  _debrisMaterials = [];
}

// ---------------------------------------------------------------------------
// 1. HE Explosion — fireball particles + point light flash + screen shake
// ---------------------------------------------------------------------------

export function spawnHEExplosion(position: THREE.Vector3, scene: THREE.Scene): void {
  const now = performance.now();

  // Screen shake — strong enough to feel the blast
  triggerScreenShake(0.12, 0.5);

  // --- Bright flash light ---
  const light = new THREE.PointLight(0xff4400, 20, 35, 2);
  light.position.copy(position);
  markEffect(light);
  scene.add(light);

  const lightStart = now;
  addEffect({
    update(now) {
      const t = (now - lightStart) / 250;
      if (t >= 1) { disposeMesh(light, scene); return false; }
      light.intensity = 20 * (1 - t * t);
      return true;
    },
  });

  // --- Fire particles ---
  const fireTex = createSpriteTexture(64, 'rgba(255,200,50,1)', 'rgba(255,80,10,0.8)', 'circle');
  const fireCount = 25 + Math.floor(Math.random() * 10);

  const fireParticles: Array<{ sprite: THREE.Sprite; velocity: THREE.Vector3; pooledMat: THREE.SpriteMaterial }> = [];
  const firePool = getFireMaterialPool();

  for (let i = 0; i < fireCount; i++) {
    const mat = firePool.acquire();
    mat.map = fireTex;
    mat.blending = THREE.AdditiveBlending;
    mat.opacity = 1;
    mat.color.setRGB(1, 1, 1);
    mat.needsUpdate = true;
    const sprite = new THREE.Sprite(mat);
    const s = rand(0.3, 0.8);
    sprite.scale.set(s, s, s);
    sprite.position.copy(position);
    markEffect(sprite);
    scene.add(sprite);

    const dir = new THREE.Vector3(
      rand(-1, 1), rand(-0.1, 1.5), rand(-1, 1),
    ).normalize().multiplyScalar(rand(5, 14));

    fireParticles.push({ sprite, velocity: dir, pooledMat: mat });
  }

  const fireStart = now;
  const FIRE_FX_DURATION = 1000;
  let fireLastTime = now;

  addEffect({
    update(now) {
      const dt = Math.min((now - fireLastTime) / 1000, 0.05);
      fireLastTime = now;
      const elapsed = now - fireStart;
      const t = Math.min(elapsed / FIRE_FX_DURATION, 1);

      for (const p of fireParticles) {
        p.velocity.y += -6 * dt;
        p.sprite.position.addScaledVector(p.velocity, dt);
        p.velocity.multiplyScalar(0.95);

        const mat = p.sprite.material as THREE.SpriteMaterial;
        mat.opacity = 1 - t;
        mat.color.setRGB(1 - t * 0.4, 0.5 - t * 0.3, 0.2 - t * 0.15);
        p.sprite.scale.multiplyScalar(1 - dt * 0.3);
      }

      if (t >= 1) {
        for (const p of fireParticles) {
          scene.remove(p.sprite);
          firePool.release(p.pooledMat);
        }
        return false;
      }
      return true;
    },
  });

  // --- Smoke column after fire ---
  setTimeout(() => {
    const smokeTex = createSpriteTexture(64, 'rgba(100,95,85,0.6)', 'rgba(60,55,50,0.2)', 'smoke');
    const smokeCount = 8;
    const smokes: THREE.Sprite[] = [];
    const heSmokePool = getHeSmokeMaterialPool();
    const heSmokePooledMats: THREE.SpriteMaterial[] = [];
    for (let i = 0; i < smokeCount; i++) {
      const mat = heSmokePool.acquire();
      mat.map = smokeTex;
      mat.opacity = 0.5;
      mat.needsUpdate = true;
      const sprite = new THREE.Sprite(mat);
      const s = rand(0.5, 1.2);
      sprite.scale.set(s, s, s);
      sprite.position.copy(position).add(new THREE.Vector3(rand(-0.8, 0.8), rand(0.5, 2.0), rand(-0.8, 0.8)));
      markEffect(sprite);
      scene.add(sprite);
      smokes.push(sprite);
      heSmokePooledMats.push(mat);
    }

    const smokeStart = performance.now();
    const SMOKE_FX_DURATION = 2000;
    let smokeColumnLastTime = smokeStart;

    addEffect({
      update(now) {
        const dt = Math.min((now - smokeColumnLastTime) / 1000, 0.05);
        smokeColumnLastTime = now;
        const elapsed = now - smokeStart;
        const t = Math.min(elapsed / SMOKE_FX_DURATION, 1);

        for (const s of smokes) {
          s.position.y += dt * 1.5;
          s.scale.x += dt * 0.3;
          s.scale.y += dt * 0.3;
          (s.material as THREE.SpriteMaterial).opacity = 0.5 * (1 - t);
        }

        if (t >= 1) {
          for (let i = 0; i < smokes.length; i++) {
            scene.remove(smokes[i]);
            heSmokePool.release(heSmokePooledMats[i]);
          }
          return false;
        }
        return true;
      },
    });
  }, 400);

  // --- Debris (shared geometries + materials) ---
  const debrisCount = 8 + Math.floor(Math.random() * 5);
  const debris: Array<{ mesh: THREE.Mesh; velocity: THREE.Vector3; spin: THREE.Vector3 }> = [];

  // F-RN-002: lazy-init via getDebrisResources() — rebuilds after a previous
  // disposeEffectPools() so the next Game() never references disposed GPU.
  const { geos: debrisGeos, mats: debrisMats } = getDebrisResources();
  for (let i = 0; i < debrisCount; i++) {
    const dGeo = debrisGeos[Math.floor(Math.random() * debrisGeos.length)];
    const dMat = debrisMats[Math.floor(Math.random() * debrisMats.length)];
    const dMesh = new THREE.Mesh(dGeo, dMat);
    dMesh.userData.isEffect = true;
    dMesh.position.copy(position);
    markEffect(dMesh);
    scene.add(dMesh);

    debris.push({
      mesh: dMesh,
      velocity: new THREE.Vector3(rand(-1, 1), rand(0.5, 1), rand(-1, 1)).normalize().multiplyScalar(rand(5, 14)),
      spin: new THREE.Vector3(rand(-15, 15), rand(-15, 15), rand(-15, 15)),
    });
  }

  const debrisStart = now;
  const DEBRIS_DURATION = 2500;
  let debrisLastTime = now;

  addEffect({
    update(now) {
      const dt = Math.min((now - debrisLastTime) / 1000, 0.05);
      debrisLastTime = now;
      const elapsed = now - debrisStart;
      const t = Math.min(elapsed / DEBRIS_DURATION, 1);

      for (const d of debris) {
        d.velocity.y += -15 * dt;
        d.mesh.position.addScaledVector(d.velocity, dt);
        d.velocity.multiplyScalar(0.99);
        d.mesh.rotation.x += d.spin.x * dt;
        d.mesh.rotation.y += d.spin.y * dt;
        d.mesh.rotation.z += d.spin.z * dt;

        if (d.mesh.position.y < 0) {
          d.mesh.position.y = 0;
          d.velocity.y = Math.abs(d.velocity.y) * 0.2;
          d.velocity.x *= 0.6;
          d.velocity.z *= 0.6;
          d.spin.multiplyScalar(0.4);
        }

        if (t > 0.75) {
          // Scale down for fade-out instead of mutating shared material opacity
          const fadeScale = 1 - (t - 0.75) / 0.25;
          d.mesh.scale.setScalar(fadeScale);
        }
      }

      if (t >= 1) {
        // Remove from scene without disposing shared geometry/material
        for (const d of debris) scene.remove(d.mesh);
        return false;
      }
      return true;
    },
  });

  // --- Scorch decal ---
  const scorchGeo = new THREE.CircleGeometry(2.5, 32);
  const scorchMat = new THREE.MeshBasicMaterial({
    color: 0x111111,
    transparent: true,
    opacity: 0.7,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const scorch = new THREE.Mesh(scorchGeo, scorchMat);
  scorch.position.copy(position);
  scorch.position.y += 0.02; // slight offset above ground
  scorch.rotation.x = -Math.PI / 2; // lay flat
  markEffect(scorch);
  scene.add(scorch);

  const scorchStart = now;
  const SCORCH_DURATION = 10000; // 10 seconds total

  addEffect({
    update(now) {
      const elapsed = (now - scorchStart) / 1000; // seconds
      if (elapsed > 8) {
        scorchMat.opacity = 0.7 * (1 - (elapsed - 8) / 2); // fade from 8-10s
      }
      if (elapsed >= 10) {
        disposeMesh(scorch, scene);
        return false;
      }
      return true;
    },
  });

  // --- Shockwave ring ---
  const ringGeo = new THREE.RingGeometry(0.1, 0.5, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xffaa44,
    transparent: true,
    opacity: 0.8,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.position.copy(position);
  ring.position.y += 0.1;
  ring.rotation.x = -Math.PI / 2;
  markEffect(ring);
  scene.add(ring);

  const ringStart = now;
  const RING_EXPAND_TIME = 400; // 0.4 seconds
  const blastRadius = HE_BLAST_RADIUS;

  addEffect({
    update(now) {
      const elapsed = now - ringStart;
      const t = elapsed / RING_EXPAND_TIME;
      const scale = t * blastRadius;
      ring.scale.set(scale, scale, 1);
      ringMat.opacity = 0.8 * (1 - t);
      if (t >= 1) {
        disposeMesh(ring, scene);
        return false;
      }
      return true;
    },
  });
}

// ---------------------------------------------------------------------------
// 2. Smoke Cloud — cluster of semi-transparent white/gray sphere sprites
// ---------------------------------------------------------------------------

export interface SmokeCloudHandle {
  remove(): void;
  isActive(): boolean;
  position: THREE.Vector3;
  radius: number;
}

// Volumetric smoke system integration
import type { VolumetricSmokeSystem } from './volumetric-smoke';
let _volumetricSmokeSystem: VolumetricSmokeSystem | null = null;
let _nextVolSmokeId = 1;
export function setVolumetricSmokeSystem(system: VolumetricSmokeSystem): void {
  _volumetricSmokeSystem = system;
}

export function spawnSmokeCloud(position: THREE.Vector3, scene: THREE.Scene): SmokeCloudHandle {
  // Delegate to volumetric smoke system if available
  if (_volumetricSmokeSystem) {
    try {
      const id = _nextVolSmokeId++;
      const volHandle = _volumetricSmokeSystem.addSmoke(id, position);
      return {
        remove: () => volHandle.remove(),
        isActive: () => volHandle.isActive,
        position: volHandle.position,
        radius: volHandle.radius,
      };
    } catch (_e) {
      // Volumetric smoke failed — fall through to sprite-based smoke
    }
  }
  const CLOUD_COUNT = 50; // increased from 40 for better density, still under 60 cap
  const EXPAND_TIME = 600; // ms to reach full size (slightly longer for stagger)
  const TOTAL_TIME = SMOKE_DURATION * 1000;
  const FADE_START = TOTAL_TIME - 2000;

  const smokeTex = createSpriteTexture(64, 'rgba(220,215,205,0.7)', 'rgba(180,175,165,0.3)', 'smoke');
  const sprites: THREE.Sprite[] = [];
  const smokePool = getSmokeMaterialPool();
  const pooledMats: THREE.SpriteMaterial[] = [];

  // Per-sprite data for organic motion and staggered expand
  const baseScales: number[] = [];
  const expandDelays: number[] = [];    // stagger: ms delay before sprite starts expanding
  const rotationSpeeds: number[] = [];  // radians per second for slow rotation

  let active = true;
  const startTime = performance.now();

  for (let i = 0; i < CLOUD_COUNT; i++) {
    const mat = smokePool.acquire();
    mat.map = smokeTex;
    mat.opacity = 0;
    mat.color.setRGB(1, 1, 1);
    mat.rotation = 0;
    mat.needsUpdate = true;
    const sprite = new THREE.Sprite(mat);
    sprite.userData.isEffect = true;
    markEffect(sprite);
    pooledMats.push(mat);

    // Pillar distribution: more sprites near center, fewer at edges
    // Use a power curve on the radius — sqrt concentrates toward center
    const rawRadius = Math.pow(Math.random(), 0.6); // 0.6 exponent = mild center-bias
    const angle = Math.random() * Math.PI * 2;
    const r = rawRadius * SMOKE_RADIUS * 0.7;

    // Layered heights: distribute sprites in 3 vertical bands for volume
    // Lower band (0 - 0.8m): ~40%, Mid band (0.8 - 1.8m): ~40%, Upper band (1.8 - 2.8m): ~20%
    let yOffset: number;
    const yRoll = Math.random();
    if (yRoll < 0.4) {
      yOffset = rand(0, 0.8);           // ground-level layer
    } else if (yRoll < 0.8) {
      yOffset = rand(0.8, 1.8);         // mid layer
    } else {
      yOffset = rand(1.8, 2.8);         // upper wisps
    }

    const offset = new THREE.Vector3(
      Math.cos(angle) * r,
      yOffset,
      Math.sin(angle) * r,
    );

    sprite.position.copy(position).add(offset);

    // Larger sprites in the center, smaller at edges — reinforces pillar effect
    const distFactor = 1 - rawRadius * 0.4; // center sprites are up to 40% larger
    const s = rand(1.8, 3.2) * distFactor;
    baseScales.push(s);
    sprite.scale.set(0, 0, 0); // start at 0 for expand animation

    // Stagger: sprites expand with random delays (0-200ms) for organic pop-in
    expandDelays.push(rand(0, 200));

    // Per-sprite rotation speed: very slow (0.05 - 0.2 rad/s)
    rotationSpeeds.push(rand(0.05, 0.2) * (Math.random() > 0.5 ? 1 : -1));

    scene.add(sprite);
    sprites.push(sprite);
  }

  let smokeCloudLastTime = startTime;

  addEffect({
    update(now) {
      if (!active) return false;

      const dt = Math.min((now - smokeCloudLastTime) / 1000, 0.05);
      smokeCloudLastTime = now;
      const elapsed = now - startTime;

      if (elapsed >= TOTAL_TIME) {
        for (let i = 0; i < sprites.length; i++) {
          scene.remove(sprites[i]);
          smokePool.release(pooledMats[i]);
        }
        active = false;
        return false;
      }

      for (let i = 0; i < sprites.length; i++) {
        const s = sprites[i];
        const mat = s.material as THREE.SpriteMaterial;

        // Staggered expand: each sprite has its own delay
        const spriteElapsed = elapsed - expandDelays[i];
        if (spriteElapsed < 0) continue; // not yet started expanding

        const expandT = Math.min(spriteElapsed / EXPAND_TIME, 1);

        // Expand phase: scale up and fade in with easing
        if (expandT < 1) {
          // Ease-out for smooth pop-in
          const eased = 1 - (1 - expandT) * (1 - expandT);
          const scale = baseScales[i] * eased;
          s.scale.set(scale, scale, scale);
          mat.opacity = eased * 0.75;
        }

        // Slow rotation for organic movement
        s.material.rotation += rotationSpeeds[i] * dt;

        // Gentle drift: slow upward + sinusoidal horizontal sway
        s.position.y += dt * 0.015;
        s.position.x += Math.sin(now * 0.0003 + i * 0.7) * dt * 0.08;
        s.position.z += Math.cos(now * 0.0004 + i * 1.3) * dt * 0.08;

        // Fade out in last 2s
        if (elapsed > FADE_START) {
          const fadeT = (elapsed - FADE_START) / 2000;
          mat.opacity = 0.75 * (1 - fadeT);
        }
      }

      return true;
    },
  });

  const handle: SmokeCloudHandle = {
    remove() {
      if (!active) return;
      active = false;
      for (let i = 0; i < sprites.length; i++) {
        scene.remove(sprites[i]);
        smokePool.release(pooledMats[i]);
      }
      unregisterSmokeCloud(fogCloud);
    },
    isActive() {
      return active;
    },
    position: position.clone(),
    radius: SMOKE_RADIUS,
  };

  // Register with the smoke fog system for interior fog overlay
  const fogCloud: SmokeFogCloud = {
    position: position.clone(),
    radius: SMOKE_RADIUS,
    isActive: () => active,
  };
  registerSmokeCloud(fogCloud);

  return handle;
}

// ---------------------------------------------------------------------------
// 3. Flash Screen Effect — white overlay that fades to transparent
// ---------------------------------------------------------------------------

/** RAF IDs for flash fade loops — cancel before starting new ones to prevent competing loops. */
let flashOverlayRafId: number | null = null;
let flashAfterimageRafId: number | null = null;

export function applyFlashEffect(blindDurationS: number): void {
  // Cancel any existing fade loops from a previous flash
  if (flashOverlayRafId !== null) {
    cancelAnimationFrame(flashOverlayRafId);
    flashOverlayRafId = null;
  }
  if (flashAfterimageRafId !== null) {
    cancelAnimationFrame(flashAfterimageRafId);
    flashAfterimageRafId = null;
  }

  // Create or reuse overlay
  let overlay = document.getElementById('flash-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'flash-overlay';
    overlay.style.cssText =
      'position:fixed;top:0;left:0;width:100vw;height:100vh;' +
      'background:white;pointer-events:none;z-index:200;' +
      'transition:none;';
    document.body.appendChild(overlay);
  }

  // Full white
  overlay.style.opacity = '1';
  overlay.style.display = 'block';

  const totalMs = blindDurationS * 1000;
  const startTime = performance.now();

  // Remove any existing afterimage canvas before creating a new one
  const existingAfterCanvas = document.getElementById('flash-afterimage');
  if (existingAfterCanvas) {
    existingAfterCanvas.remove();
  }

  // Afterimage burn-in effect
  const gameCanvas = document.querySelector('canvas');
  if (gameCanvas) {
    const afterCanvas = document.createElement('canvas');
    afterCanvas.id = 'flash-afterimage';
    afterCanvas.width = gameCanvas.width;
    afterCanvas.height = gameCanvas.height;
    afterCanvas.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      z-index: 199; pointer-events: none; opacity: 0.6;
      mix-blend-mode: screen;
    `;
    const ctx2d = afterCanvas.getContext('2d');
    if (ctx2d) {
      try {
        ctx2d.drawImage(gameCanvas, 0, 0);
      } catch (_e) {
        // drawImage may fail if preserveDrawingBuffer is false
      }
      document.body.appendChild(afterCanvas);

      // Fade the afterimage over the flash duration
      const afterStart = performance.now();
      const duration = blindDurationS;
      const fadeAfterimage = (): void => {
        const elapsed = (performance.now() - afterStart) / 1000;
        const t = elapsed / duration;
        afterCanvas.style.opacity = String(0.6 * (1 - t));
        if (t < 1) {
          flashAfterimageRafId = requestAnimationFrame(fadeAfterimage);
        } else {
          afterCanvas.remove();
          flashAfterimageRafId = null;
        }
      };
      flashAfterimageRafId = requestAnimationFrame(fadeAfterimage);
    }
  }

  function fade(): void {
    const elapsed = performance.now() - startTime;
    const t = Math.min(elapsed / totalMs, 1);

    // Quick initial flash, then slow decay
    const opacity = Math.pow(1 - t, 2);
    overlay!.style.opacity = String(opacity);

    if (t < 1) {
      flashOverlayRafId = requestAnimationFrame(fade);
    } else {
      overlay!.style.display = 'none';
      flashOverlayRafId = null;
    }
  }

  flashOverlayRafId = requestAnimationFrame(fade);
}

// ---------------------------------------------------------------------------
// 4. Fire Zone — animated ground fire with flickering point light
// ---------------------------------------------------------------------------

export interface FireZoneHandle {
  update(dt: number): boolean; // returns false when expired
  remove(): void;
}

export function spawnFireZone(
  position: THREE.Vector3,
  radius: number,
  scene: THREE.Scene,
): FireZoneHandle {
  if (!scene) return { update: () => false, remove: () => {} };

  const TOTAL_TIME = FIRE_DURATION * 1000;
  const startTime = performance.now();
  let active = true;

  // Fire ring of sprites on the ground
  const fireTex = createSpriteTexture(64, 'rgba(255,160,30,1)', 'rgba(255,60,10,0.8)', 'circle');
  const FIRE_COUNT = 16;
  const sprites: THREE.Sprite[] = [];
  const fzFirePool = getFireMaterialPool();
  const fzPooledMats: THREE.SpriteMaterial[] = [];

  for (let i = 0; i < FIRE_COUNT; i++) {
    const angle = (i / FIRE_COUNT) * Math.PI * 2;
    const r = rand(0.3, 1) * radius;
    const mat = fzFirePool.acquire();
    mat.map = fireTex;
    mat.blending = THREE.AdditiveBlending;
    mat.opacity = 0.8;
    mat.color.setRGB(1, 1, 1);
    mat.needsUpdate = true;
    const sprite = new THREE.Sprite(mat);
    const s = rand(0.8, 1.5);
    sprite.scale.set(s, s * 1.5, s); // taller than wide

    // Store target position, start at center for spread animation
    const targetX = position.x + Math.cos(angle) * r;
    const targetZ = position.z + Math.sin(angle) * r;
    sprite.position.set(position.x, position.y + rand(0.1, 0.6), position.z);
    sprite.userData.targetX = targetX;
    sprite.userData.targetZ = targetZ;

    sprite.userData.isEffect = true;
    markEffect(sprite);
    scene.add(sprite);
    sprites.push(sprite);
    fzPooledMats.push(mat);
  }

  // Inner fill sprites
  for (let i = 0; i < 8; i++) {
    const mat = fzFirePool.acquire();
    mat.map = fireTex;
    mat.blending = THREE.AdditiveBlending;
    mat.opacity = 0.6;
    mat.color.setRGB(1, 1, 1);
    mat.needsUpdate = true;
    const sprite = new THREE.Sprite(mat);
    const s = rand(0.5, 1.0);
    sprite.scale.set(s, s * 1.3, s);

    // Store target position, start at center for spread animation
    const targetX = position.x + rand(-radius * 0.5, radius * 0.5);
    const targetZ = position.z + rand(-radius * 0.5, radius * 0.5);
    sprite.position.set(position.x, position.y + rand(0.1, 0.4), position.z);
    sprite.userData.targetX = targetX;
    sprite.userData.targetZ = targetZ;

    sprite.userData.isEffect = true;
    markEffect(sprite);
    scene.add(sprite);
    sprites.push(sprite);
    fzPooledMats.push(mat);
  }

  // Flickering point light
  const light = new THREE.PointLight(0xff6600, 4, radius * 2, 2);
  light.position.copy(position);
  light.position.y += 0.5;
  markEffect(light);
  scene.add(light);

  let fireZoneLastTime = startTime;

  addEffect({
    update(now) {
      if (!active) return false;

      const dt = Math.min((now - fireZoneLastTime) / 1000, 0.05);
      fireZoneLastTime = now;
      const elapsed = now - startTime;
      if (elapsed >= TOTAL_TIME) {
        cleanup();
        return false;
      }

      // Flicker light
      light.intensity = 3 + Math.sin(now * 0.01) * 1.5 + Math.sin(now * 0.023) * 0.8;

      // Animate fire sprites (bob up and down, flicker opacity)
      for (let i = 0; i < sprites.length; i++) {
        const s = sprites[i];
        const mat = s.material as THREE.SpriteMaterial;

        // Spread animation (first 0.5 seconds)
        if (elapsed < 500) {
          const spreadT = elapsed / 500;
          const eased = spreadT * spreadT * (3 - 2 * spreadT); // smoothstep
          s.position.x = position.x + ((s.userData.targetX as number) - position.x) * eased;
          s.position.z = position.z + ((s.userData.targetZ as number) - position.z) * eased;
        }

        s.position.y += Math.sin(now * 0.005 + i * 0.7) * dt * 0.3;
        mat.opacity = 0.5 + Math.sin(now * 0.008 + i * 1.1) * 0.3;

        // Color shift
        mat.color.setRGB(
          1,
          0.3 + Math.sin(now * 0.006 + i) * 0.15,
          0.1,
        );
      }

      // Fade out in last 1.5s
      const fadeStart = TOTAL_TIME - 1500;
      if (elapsed > fadeStart) {
        const fadeT = (elapsed - fadeStart) / 1500;
        for (const s of sprites) {
          (s.material as THREE.SpriteMaterial).opacity *= (1 - fadeT);
        }
        light.intensity *= (1 - fadeT);
      }

      return true;
    },
  });

  function cleanup(): void {
    if (!active) return;
    active = false;
    for (let i = 0; i < sprites.length; i++) {
      scene.remove(sprites[i]);
      fzFirePool.release(fzPooledMats[i]);
    }
    disposeMesh(light, scene);
  }

  return {
    update(_dt: number): boolean {
      const elapsed = performance.now() - startTime;
      return active && elapsed < TOTAL_TIME;
    },
    remove(): void {
      cleanup();
    },
  };
}

// ---------------------------------------------------------------------------
// 5. Decoy Grenade — small mesh + periodic fake gunfire sound + expiry puff
// ---------------------------------------------------------------------------

export interface DecoyHandle {
  update(dt: number): boolean; // returns false when expired
  remove(): void;
}

/**
 * Create a decoy grenade effect at the given position.
 * Places a small blinking mesh and periodically plays a random weapon fire
 * sound via the provided `playPositionalSound` callback.
 *
 * @param position World position where the decoy landed
 * @param scene Three.js scene to add meshes to
 * @param playPositionalSound Optional callback to play a sound at a 3D position.
 *        Signature: (position: THREE.Vector3) => void
 */
export function createDecoyEffect(
  position: THREE.Vector3,
  scene: THREE.Scene,
  playPositionalSound?: (position: THREE.Vector3) => void,
): DecoyHandle {
  if (!scene) return { update: () => false, remove: () => {} };

  const TOTAL_TIME = DECOY_DURATION * 1000;
  const GUNFIRE_INTERVAL = DECOY_GUNFIRE_INTERVAL; // ms
  const startTime = performance.now();
  let active = true;
  let lastGunfireTime = startTime;

  // Small cylinder to represent the decoy grenade on the ground
  const bodyGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.12, 8);
  const bodyMat = new THREE.MeshLambertMaterial({ color: 0x556b2f });
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.copy(position);
  body.position.y += 0.06; // half height above ground
  markEffect(body);
  scene.add(body);

  // Blinking indicator light on top
  const indicatorGeo = new THREE.SphereGeometry(0.02, 6, 6);
  const indicatorMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
  const indicator = new THREE.Mesh(indicatorGeo, indicatorMat);
  indicator.position.copy(position);
  indicator.position.y += 0.14;
  markEffect(indicator);
  scene.add(indicator);

  // Small point light for the blinking effect
  const light = new THREE.PointLight(0xff4400, 0, 3, 2);
  light.position.copy(position);
  light.position.y += 0.15;
  markEffect(light);
  scene.add(light);

  addEffect({
    update(now) {
      if (!active) return false;

      const elapsed = now - startTime;

      if (elapsed >= TOTAL_TIME) {
        // Expiry: spawn a small smoke puff
        spawnDecoyExpiryPuff(position, scene);
        cleanup();
        return false;
      }

      // Blink the indicator light (~4Hz)
      const blinkOn = Math.sin(now * 0.025) > 0;
      indicatorMat.color.setHex(blinkOn ? 0xff0000 : 0x330000);
      light.intensity = blinkOn ? 1.5 : 0;

      // Periodic fake gunfire sound
      if (now - lastGunfireTime >= GUNFIRE_INTERVAL) {
        lastGunfireTime = now;
        if (playPositionalSound) {
          playPositionalSound(position);
        }
      }

      return true;
    },
  });

  function cleanup(): void {
    if (!active) return;
    active = false;
    disposeMesh(body, scene);
    disposeMesh(indicator, scene);
    disposeMesh(light, scene);
  }

  return {
    update(_dt: number): boolean {
      const elapsed = performance.now() - startTime;
      return active && elapsed < TOTAL_TIME;
    },
    remove(): void {
      cleanup();
    },
  };
}

/** Small smoke puff when decoy expires — reuses HE smoke pool. */
function spawnDecoyExpiryPuff(position: THREE.Vector3, scene: THREE.Scene): void {
  const smokeTex = createSpriteTexture(32, 'rgba(150,145,135,0.5)', 'rgba(100,95,85,0.2)', 'smoke');
  const heSmokePool = getHeSmokeMaterialPool();
  const count = 4;
  const sprites: THREE.Sprite[] = [];
  const mats: THREE.SpriteMaterial[] = [];

  for (let i = 0; i < count; i++) {
    const mat = heSmokePool.acquire();
    mat.map = smokeTex;
    mat.opacity = 0.4;
    mat.needsUpdate = true;
    const sprite = new THREE.Sprite(mat);
    const s = rand(0.15, 0.3);
    sprite.scale.set(s, s, s);
    sprite.position.copy(position).add(
      new THREE.Vector3(rand(-0.15, 0.15), rand(0.1, 0.4), rand(-0.15, 0.15)),
    );
    markEffect(sprite);
    scene.add(sprite);
    sprites.push(sprite);
    mats.push(mat);
  }

  const puffStart = performance.now();
  const PUFF_DURATION = 800;

  addEffect({
    update(now) {
      const elapsed = now - puffStart;
      const t = Math.min(elapsed / PUFF_DURATION, 1);

      for (const s of sprites) {
        s.position.y += 0.003;
        s.scale.x += 0.002;
        s.scale.y += 0.002;
        (s.material as THREE.SpriteMaterial).opacity = 0.4 * (1 - t);
      }

      if (t >= 1) {
        for (let i = 0; i < sprites.length; i++) {
          scene.remove(sprites[i]);
          heSmokePool.release(mats[i]);
        }
        return false;
      }
      return true;
    },
  });
}
